/**
 * CSS Sanitization Utilities for Email Content
 *
 * This module provides secure CSS sanitization to prevent CSS injection attacks
 * while preserving legitimate email styling and layout.
 */

// Dangerous CSS patterns that can lead to XSS or data exfiltration
const DANGEROUS_CSS_PATTERNS = [
  // JavaScript execution
  /javascript:/gi,
  /expression\s*\(/gi,
  /behavior\s*:/gi,
  /vbscript:/gi,

  // Data URLs with scripts
  /url\s*\(\s*["']?data:.*?script/gi,
  /url\s*\(\s*["']?data:.*?javascript/gi,

  // Note: @import statements are handled separately in the parsing phase

  // Browser-specific dangerous properties
  /-moz-binding/gi,
  /binding\s*:/gi,
  /-webkit-binding/gi,

  // CSS expressions (IE legacy)
  /expression\s*\(/gi,

  // Attribute selectors that could be used for data theft
  /\[.*\*=.*\]/g,
  /\[.*\^=.*\]/g,
  /\[.*\$=.*\]/g,
];

// Properties that are safe for email styling
const ALLOWED_CSS_PROPERTIES = new Set([
  // Text styling
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-align',
  'text-decoration',
  'text-transform',
  'line-height',
  'letter-spacing',
  'word-spacing',

  // Background and borders
  'background',
  'background-color',
  'background-image',
  'background-repeat',
  'background-position',
  'background-size',
  'border',
  'border-color',
  'border-width',
  'border-style',
  'border-radius',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',

  // Layout and positioning
  'display',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'float',
  'clear',
  'overflow',
  'visibility',
  'z-index',

  // Box model
  'width',
  'height',
  'min-width',
  'min-height',
  'max-width',
  'max-height',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',

  // Table styling (critical for email layout)
  'border-collapse',
  'border-spacing',
  'caption-side',
  'empty-cells',
  'table-layout',
  'vertical-align',

  // List styling
  'list-style',
  'list-style-type',
  'list-style-position',
  'list-style-image',

  // Transform (limited safe subset)
  'transform',

  // Flexbox (modern email support)
  'flex',
  'flex-direction',
  'flex-wrap',
  'justify-content',
  'align-items',
  'align-content',

  // CSS Grid (modern email support)
  'grid',
  'grid-template-columns',
  'grid-template-rows',
  'grid-gap',
  'grid-column',
  'grid-row',
]);

/**
 * Validates CSS property names against allowlist
 */
const isAllowedProperty = (property: string): boolean => {
  const cleanProperty = property.trim().toLowerCase();
  return ALLOWED_CSS_PROPERTIES.has(cleanProperty);
};

/**
 * Validates CSS values for dangerous patterns
 */
const isValidValue = (value: string): boolean => {
  // Check for dangerous patterns in values
  const hasDangerousPattern = DANGEROUS_CSS_PATTERNS.some((pattern) =>
    pattern.test(value)
  );

  // Also check for common XSS/script-related terms
  const dangerousTerms = [
    'alert',
    'eval',
    'document',
    'window',
    'script',
    'onload',
    'onerror',
  ];
  const hasScriptTerms = dangerousTerms.some((term) =>
    value.toLowerCase().includes(term.toLowerCase())
  );

  return !hasDangerousPattern && !hasScriptTerms;
};

/**
 * Validates CSS selectors to prevent data exfiltration selectors
 */
const isValidSelector = (selector: string): boolean => {
  const cleanSelector = selector.trim();

  // Prevent complex attribute selectors that could be used for data theft
  if (/\[.*[*^$]=/.test(cleanSelector)) {
    return false;
  }

  // Prevent universal selectors that could affect the entire page
  if (cleanSelector === '*' || cleanSelector.startsWith('* ')) {
    return false;
  }

  // Allow standard CSS selectors including pseudo-classes, element names, etc.
  // This is more permissive than the previous version
  return /^[a-zA-Z0-9\s.#:_\-(),]+$/.test(cleanSelector);
};

/**
 * Parses and validates individual CSS declarations
 */
const sanitizeDeclaration = (declaration: string): string => {
  const colonIndex = declaration.indexOf(':');
  if (colonIndex === -1) return '';

  const property = declaration.slice(0, colonIndex).trim();
  const value = declaration.slice(colonIndex + 1).trim();

  if (!isAllowedProperty(property) || !isValidValue(value)) {
    return '';
  }

  return `${property}: ${value}`;
};

/** Split a selector list on top-level commas, leaving `:not(a, b)` whole. */
const splitSelectorList = (selectorList: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of selectorList) {
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;
    if (char === ',' && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
};

const DOCUMENT_ROOT_PREFIX = /^((html|body|:root)(\s+|$))+/i;

/**
 * Confine a selector list to the email's own container. An email's
 * stylesheet is written for a whole document, so `a:link` or `body` would
 * otherwise restyle the console around it. `html`, `body` and `:root` mean
 * the container itself.
 */
export const scopeSelector = (selectorList: string, scope: string): string =>
  splitSelectorList(selectorList)
    .map((selector) => {
      const rest = selector.replace(DOCUMENT_ROOT_PREFIX, '');
      return rest ? `${scope} ${rest}` : scope;
    })
    .join(', ');

interface CssBlock {
  readonly prelude: string;
  readonly body: string;
}

/**
 * Split a stylesheet into its top-level `prelude { body }` blocks, keeping
 * nested braces inside the body. An unclosed trailing block is dropped.
 */
const splitTopLevelBlocks = (css: string): CssBlock[] => {
  const blocks: CssBlock[] = [];
  let depth = 0;
  let preludeStart = 0;
  let bodyStart = 0;
  let prelude = '';
  for (let i = 0; i < css.length; i += 1) {
    const char = css[i];
    if (char === '{') {
      if (depth === 0) {
        prelude = css.slice(preludeStart, i).trim();
        bodyStart = i + 1;
      }
      depth += 1;
    } else if (char === '}') {
      if (depth === 0) {
        preludeStart = i + 1;
      } else {
        depth -= 1;
        if (depth === 0) {
          blocks.push({ prelude, body: css.slice(bodyStart, i) });
          preludeStart = i + 1;
        }
      }
    }
  }
  return blocks;
};

const MEDIA_LENGTH = /^\d+(\.\d+)?(px|em|rem)$|^0$/;

/** Media features an email may condition on, and the values each accepts. */
const ALLOWED_MEDIA_FEATURES: Record<string, RegExp> = {
  'min-width': MEDIA_LENGTH,
  'max-width': MEDIA_LENGTH,
  'min-height': MEDIA_LENGTH,
  'max-height': MEDIA_LENGTH,
  orientation: /^(portrait|landscape)$/,
  'prefers-color-scheme': /^(light|dark)$/,
};

const MEDIA_TYPE = /^(only\s+)?screen$/;
const MEDIA_FEATURE = /^\(\s*([a-z-]+)\s*:\s*([^()]+?)\s*\)$/;

/** One media query, rebuilt from its allowed parts; null if any part is not allowed. */
const sanitizeMediaQuery = (query: string): string | null => {
  const parts = query
    .trim()
    .toLowerCase()
    .split(/\s*\band\b\s*/);
  const sanitized = parts.map((part, index) => {
    if (index === 0 && MEDIA_TYPE.test(part)) {
      return part.replace(/\s+/, ' ');
    }
    const feature = MEDIA_FEATURE.exec(part);
    if (feature && ALLOWED_MEDIA_FEATURES[feature[1]]?.test(feature[2])) {
      return `(${feature[1]}: ${feature[2]})`;
    }
    return null;
  });
  if (sanitized.some((part) => part === null)) {
    return null;
  }
  return sanitized.join(' and ');
};

/** A media query list, rebuilt from allowed parts; null if any query is not allowed. */
export const sanitizeMediaQueryList = (queryList: string): string | null => {
  const queries = queryList.split(',').map(sanitizeMediaQuery);
  if (queries.some((query) => query === null)) {
    return null;
  }
  return queries.join(', ');
};

/** One style rule, scoped to the email; null if nothing in it survives. */
const sanitizeRule = (
  { prelude, body }: CssBlock,
  scope: string
): string | null => {
  // A nested block (CSS nesting, or an at-rule's contents) is not a style rule.
  if (body.includes('{') || !isValidSelector(prelude)) {
    return null;
  }

  const declarations = body
    .split(';')
    .map((decl) => sanitizeDeclaration(decl))
    .filter((decl) => decl.length > 0);

  if (declarations.length === 0) {
    return null;
  }
  return `${scopeSelector(prelude, scope)} { ${declarations.join('; ')} }`;
};

/**
 * Keep an `@media` block whose query passes the allowlist, with its rules
 * sanitized and scoped, so a mobile layout stays mobile-only. Every other
 * at-rule block (`@font-face`, `@keyframes`, `@supports`, nested `@media`)
 * is dropped with its contents.
 */
const sanitizeAtRule = (
  { prelude, body }: CssBlock,
  scope: string
): string[] => {
  const media = /^@media\b\s*([\s\S]+)$/i.exec(prelude);
  const query = media ? sanitizeMediaQueryList(media[1]) : null;
  if (!query) {
    return [];
  }

  const rules = splitTopLevelBlocks(body)
    .filter((block) => !block.prelude.startsWith('@'))
    .map((block) => sanitizeRule(block, scope))
    .filter((rule): rule is string => rule !== null);

  return rules.length > 0 ? [`@media ${query} {\n${rules.join('\n')}\n}`] : [];
};

/**
 * Sanitizes CSS content by removing dangerous patterns and validating properties
 * @param css - Raw CSS content from email
 * @param scope - Selector of the element the email renders in; every rule is
 *   confined to it
 * @returns Sanitized CSS safe for injection
 */
export const sanitizeEmailCSS = (css: string, scope: string): string => {
  if (!css || typeof css !== 'string') {
    return '';
  }

  // Remove comments first (they could hide dangerous code). A comment
  // separates tokens, so it becomes a space: `and/**/(` stays `and (`.
  let sanitized = css.replace(/\/\*[\s\S]*?\*\//g, ' ');

  // Remove dangerous patterns
  DANGEROUS_CSS_PATTERNS.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, '');
  });

  // Remove statement at-rules; block at-rules are handled in sanitizeAtRule
  sanitized = sanitized.replace(/@import[^;]*;/gi, '');
  sanitized = sanitized.replace(/@charset[^;]*;/gi, '');

  return splitTopLevelBlocks(sanitized)
    .flatMap((block) => {
      if (block.prelude.startsWith('@')) {
        return sanitizeAtRule(block, scope);
      }
      const rule = sanitizeRule(block, scope);
      return rule ? [rule] : [];
    })
    .join('\n');
};

/**
 * Safely extracts and sanitizes CSS from email HTML content
 * @param htmlContent - Raw HTML content containing <style> tags
 * @param scope - Selector of the element the email renders in
 * @returns Sanitized CSS safe for injection
 */
export const extractAndSanitizeEmailCSS = (
  htmlContent: string,
  scope: string
): string => {
  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  const matches = htmlContent.match(styleTagRegex);

  if (!matches) return '';

  // Extract and combine all CSS content
  const rawCSS = matches
    .map((match) => match.replace(/<\/?style[^>]*>/gi, ''))
    .join('\n')
    .trim();

  // Sanitize the combined CSS
  return sanitizeEmailCSS(rawCSS, scope);
};
